import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { ProductTile } from './ProductTile';
import { COLLECTIONS } from '@/data/catalog';
import { useProducts } from '@/context/ProductsContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useJsonLd } from '@/hooks/useJsonLd';

const HERO_IMAGE =
  'https://customer-assets.emergentagent.com/job_gallerystore/artifacts/8e43o2fp_Pink%20Cowboy.jpg';

export const EditorialHome = () => {
  usePageMeta({
    title: 'Graves Collective — The Studio Shop of Joseph Graves Jr.',
    description:
      'Original paintings, signed prints, illustrated books, and wearable art from the studio of Joseph Graves Jr.',
    image: HERO_IMAGE,
  });

  useJsonLd(
    {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'Graves Collective',
          url: 'https://gravescollective.com',
          logo: 'https://customer-assets.emergentagent.com/job_9cdf2342-a607-473f-8553-b1e4a60c73ce/artifacts/6el8ycim_Graves%20Collective%20Logo.png',
          founder: { '@type': 'Person', name: 'Joseph Graves Jr.' },
          sameAs: ['https://josephgravesjr.com'],
        },
        {
          '@type': 'WebSite',
          name: 'Graves Collective',
          url: 'https://gravescollective.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://gravescollective.com/studio?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        },
      ],
    },
    'ld-json-home'
  );

  const { getProductsByCollection } = useProducts();
  const books = getProductsByCollection('books');

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="page-home">
      <SiteHeader />

      {/* HERO — asymmetric 60/40 */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-24 pb-24 lg:pb-36"
        data-testid="hero-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7 lg:pr-8">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-8">
              Studio Shop · Est. 2025
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95] tracking-tight text-ink">
              Art rooted in
              <br />
              Black experience.
              <br />
              <span className="italic text-terracotta">Worn in Denver.</span>
            </h1>
            <p className="mt-10 text-lg lg:text-xl text-soft max-w-xl leading-relaxed">
              The studio shop of Joseph Graves Jr. — wearable art, illustrated
              books, signed prints, and originals from a Black Western studio
              in Denver.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-8">
              {COLLECTIONS.map((c) => (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors"
                  data-testid={`hero-cta-${c.slug}`}
                >
                  {c.name}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-terracotta/10 rounded-full -z-0 hidden lg:block" />
            <div className="relative overflow-hidden aspect-[4/5] bg-ink/[0.04]">
              <img
                src={HERO_IMAGE}
                alt="The Pink Cowboy"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-soft">
              Featured · The Pink Cowboy
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED PRINTS — surface prints right after the hero */}
      <FeaturedPrints />

      {/* FEATURED BOOKS — mirror strip for the two children's books + bundle */}
      <FeaturedBooks />

      {/* THE COLLECTIONS — three tiles */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-24 lg:pb-36"
        data-testid="collections-section"
      >
        <div className="flex items-end justify-between mb-14 lg:mb-20">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-4">
              The Collections
            </p>
            <h2 className="font-display text-4xl lg:text-5xl text-ink tracking-tight">
              Four doors into the studio.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
          {COLLECTIONS.map((c, idx) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              className="group block"
              data-testid={`collection-tile-${c.slug}`}
            >
              <div className="relative overflow-hidden aspect-[4/5] bg-ink/[0.04]">
                <img
                  src={c.feature_image}
                  alt={c.name}
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/0 to-ink/0" />
                <div className="absolute bottom-0 inset-x-0 p-6 lg:p-8 flex items-end justify-between text-cream">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] opacity-80 mb-2">
                      0{idx + 1} · Collection
                    </p>
                    <h3 className="font-display text-3xl lg:text-4xl leading-none">
                      {c.name}
                    </h3>
                  </div>
                  <ArrowUpRight className="w-6 h-6 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                </div>
              </div>
              <p className="mt-5 text-sm text-soft leading-relaxed max-w-sm">
                {c.tagline}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* EDITORIAL LIFESTYLE BAND — full bleed */}
      <section
        className="relative w-full bg-ink text-cream overflow-hidden"
        data-testid="lifestyle-section"
      >
        <div className="relative h-[70vh] min-h-[520px] lg:h-[88vh] lg:min-h-[640px]">
          <img
            src="https://customer-assets.emergentagent.com/job_9cdf2342-a607-473f-8553-b1e4a60c73ce/artifacts/gxbo3noe_back%20of%20hoodie.png"
            alt="Black Cowboys Exist — worn in the wild"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-ink/40" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1400px] mx-auto w-full px-6 sm:px-10 lg:px-16 pb-14 lg:pb-20">
              <div className="max-w-2xl">
                <p className="text-[11px] uppercase tracking-[0.24em] text-cream/70 mb-5">
                  Worn in the wild
                </p>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight">
                  The work doesn’t belong on a shelf.{' '}
                  <span className="italic text-terracotta-soft">
                    It belongs on you.
                  </span>
                </h2>
                <Link
                  to="/apparel"
                  className="mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-cream hover:text-terracotta-soft transition-colors group"
                  data-testid="lifestyle-cta"
                >
                  Shop apparel
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EDITORIAL CALLOUT — Books feature */}
      {books.length > 0 && (
        <section
          className="bg-ink text-cream py-24 lg:py-32"
          data-testid="books-callout"
        >
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
              <div className="lg:col-span-5 lg:col-start-1">
                <div className="grid grid-cols-2 gap-4 lg:gap-6">
                  {books.slice(0, 2).map((b) => (
                    <Link
                      key={b.slug}
                      to={`/products/${b.slug}`}
                      className="block group overflow-hidden aspect-[3/4] bg-cream-deep"
                      data-testid={`books-callout-tile-${b.slug}`}
                    >
                      <img
                        src={b.image}
                        alt={b.title}
                        className="w-full h-full object-contain p-3 lg:p-4 transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                      />
                    </Link>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <p className="text-[11px] uppercase tracking-[0.24em] text-cream/60 mb-6">
                  Featured Collection
                </p>
                <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight">
                  Stories painted{' '}
                  <span className="italic text-terracotta-soft">
                    for small hands.
                  </span>
                </h2>
                <p className="mt-8 text-cream/75 text-lg leading-relaxed max-w-xl">
                  Hardcover picture books written and illustrated by Joseph
                  Graves Jr. Each copy is signed and shipped from the studio.
                </p>
                <Link
                  to="/books"
                  className="mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-cream hover:text-terracotta-soft transition-colors group"
                  data-testid="books-callout-cta"
                >
                  Explore the books
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FRAMED PRINTS — Level Frames handoff */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 lg:py-36"
        data-testid="framed-prints-callout"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 lg:col-start-1 order-2 lg:order-1">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
              Also from the studio
            </p>
            <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight text-ink">
              Museum-quality{' '}
              <span className="italic text-terracotta">framed prints,</span>
              <br />
              ready to hang.
            </h2>
            <p className="mt-8 text-soft text-lg leading-relaxed max-w-xl">
              Forty-plus signed pieces available as custom-framed prints through
              Level Frames. Choose your frame, glass, and mat — museum finishing,
              made to order, shipped ready to hang.
            </p>
            <a
              href="https://josephgravesart.level.press/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] bg-ink text-cream hover:bg-terracotta transition-colors px-8 py-4 group"
              data-testid="framed-prints-cta"
            >
              Browse framed prints
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              <div className="overflow-hidden aspect-[3/4] bg-ink/[0.04]">
                <img
                  src="https://d29mtkonxnc5fw.cloudfront.net/thumbs/EmAXdp9ujJImYsfmJjSsyg.jpg"
                  alt="Cruise — framed print"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden aspect-[3/4] bg-ink/[0.04] mt-8">
                <img
                  src="https://d29mtkonxnc5fw.cloudfront.net/thumbs/0Dn4cIhVeJd36n5o-iThGQ.jpg"
                  alt="Chocolate Honey — framed print"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden aspect-[3/4] bg-ink/[0.04]">
                <img
                  src="https://d29mtkonxnc5fw.cloudfront.net/thumbs/QpL0SgtM_RNl7e1YU8Qilg.jpg"
                  alt="When There's Smoke — framed print"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden aspect-[3/4] bg-ink/[0.04] mt-8">
                <img
                  src="https://d29mtkonxnc5fw.cloudfront.net/thumbs/ksr5UL_YmxiRRgm-2VCmSw.jpg"
                  alt="The Sunflower Between Us — framed print"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STUDIO NOTE */}
      <section
        className="max-w-3xl mx-auto px-6 sm:px-10 py-24 lg:py-36 text-center"
        data-testid="studio-note"
      >
        <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-8">
          From the studio
        </p>
        <p className="font-display text-2xl lg:text-3xl leading-[1.4] text-ink italic">
          “The work is made by hand, slowly. Every print, every page, every
          stitched garment passes through the studio before it reaches you.”
        </p>
        <p className="mt-6 text-sm text-soft uppercase tracking-[0.18em]">
          — Joseph Graves Jr.
        </p>
        <Link
          to="/about"
          className="mt-12 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
          data-testid="about-link"
        >
          About the studio
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </section>

      <SiteFooter />
    </div>
  );
};

export default EditorialHome;

// ---------------------------------------------------------------------------
// FeaturedPrints — 3-tile strip right after the hero so prints are visible fast.
// Picks the first 3 prints (both limited + open) that aren't sold.
// ---------------------------------------------------------------------------
const FEATURED_IDS = ['lep-001', 'pr-005', 'lep-004']; // Pink Cowboy, Black Gold, Sunflower Field
const FEATURED_BOOK_IDS = ['cb-001', 'cb-002', 'cb-003']; // Cowboy Capers, You Are An Artist, Bundle

const FeaturedPrints = () => {
  const { getProductsByCollection } = useProducts();
  const studio = getProductsByCollection('studio');
  const featured = FEATURED_IDS
    .map((id) => studio.find((p) => p.id === id))
    .filter(Boolean);
  const backup = studio.filter(
    (p) => (p.originalCategory === 'limited-edition-prints' || p.originalCategory === 'prints') && p.price && !p.sold
  );
  while (featured.length < 3 && backup.length) {
    const next = backup.shift();
    if (next && !featured.find((f) => f.id === next.id)) featured.push(next);
  }
  if (featured.length === 0) return null;
  return (
    <section
      className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-24 lg:pb-32"
      data-testid="featured-prints"
    >
      <div className="flex items-end justify-between mb-10 lg:mb-14">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-4">
            From the Studio
          </p>
          <h2 className="font-display text-4xl lg:text-5xl text-ink tracking-tight">
            Prints, signed and ready to ship.
          </h2>
        </div>
        <Link
          to="/studio"
          className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
          data-testid="featured-prints-see-all"
        >
          See all prints
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
        {featured.slice(0, 3).map((p, idx) => (
          <ProductTile key={p.id} product={p} eager={idx === 0} />
        ))}
      </div>

      <div className="mt-10 sm:hidden">
        <Link
          to="/studio"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
        >
          See all prints
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// FeaturedBooks — 3-tile strip so both children's books + the bundle get real
// weight on the home page.
// ---------------------------------------------------------------------------
const FeaturedBooks = () => {
  const { getProductsByCollection } = useProducts();
  const books = getProductsByCollection('books');
  const featured = FEATURED_BOOK_IDS
    .map((id) => books.find((p) => p.id === id))
    .filter(Boolean);
  if (featured.length === 0) return null;
  return (
    <section
      className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pb-24 lg:pb-32"
      data-testid="featured-books"
    >
      <div className="flex items-end justify-between mb-10 lg:mb-14">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-4">
            For young readers
          </p>
          <h2 className="font-display text-4xl lg:text-5xl text-ink tracking-tight">
            Signed picture books from the studio.
          </h2>
        </div>
        <Link
          to="/books"
          className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
          data-testid="featured-books-see-all"
        >
          See all books
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
        {featured.slice(0, 3).map((p) => (
          <ProductTile key={p.id} product={p} />
        ))}
      </div>

      <div className="mt-10 sm:hidden">
        <Link
          to="/books"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
        >
          See all books
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
};

